import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Users, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersTab({ 
  currentUser,
  users, 
  events, 
  fetchUsers 
}) {
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'judge',
    event_access: []
  });

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users`, userForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User created successfully');
      setShowUserModal(false);
      setUserForm({ name: '', email: '', password: '', role: 'judge', event_access: [] });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User updated successfully');
      setShowUserModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditUserModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setUserForm({
      name: userToEdit.name,
      email: userToEdit.email,
      password: '',
      role: userToEdit.role,
      event_access: userToEdit.event_access || []
    });
    setShowUserModal(true);
  };

  const openCreateUserModal = () => {
    setSelectedUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'judge', event_access: [] });
    setShowUserModal(true);
  };

  return (
    <>
      {/* Users Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Users & Judges</h2>
          <p className="text-gray-700 text-sm">Manage judge accounts and user access</p>
        </div>
        <Button onClick={openCreateUserModal} className="bg-pink-600 hover:bg-pink-700" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700 text-sm">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{users.length}</p>
            </div>
            <Users className="w-12 h-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700 text-sm">Judges</p>
              <p className="text-3xl font-bold text-green-500">{users.filter(u => u.role === 'judge').length}</p>
            </div>
            <Users className="w-12 h-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700 text-sm">Admins</p>
              <p className="text-3xl font-bold text-purple-500">{users.filter(u => u.role === 'admin' || u.role === 'super_admin').length}</p>
            </div>
            <Users className="w-12 h-12 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Event Access</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-700">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-testid={`user-row-${u.email}`}>
                    <td className="px-6 py-4 text-gray-900 font-medium">{u.name}</td>
                    <td className="px-6 py-4 text-gray-900">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        u.role === 'super_admin' 
                          ? 'bg-purple-500/20 text-purple-600 border border-purple-500/30'
                          : u.role === 'admin'
                          ? 'bg-blue-500/20 text-blue-600 border border-blue-500/30'
                          : 'bg-green-500/20 text-green-600 border border-green-500/30'
                      }`}>
                        {u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'Judge'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-900">
                      {u.role === 'judge' ? (
                        u.event_access?.length > 0 ? (
                          <span className="text-sm text-gray-900">
                            {u.event_access.map(eventId => {
                              const event = events.find(e => e.id === eventId);
                              return event?.name || eventId;
                            }).join(', ')}
                          </span>
                        ) : (
                          <span className="text-gray-600 text-sm italic">No events assigned</span>
                        )
                      ) : (
                        <span className="text-gray-700 text-sm font-medium">All events</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditUserModal(u)}
                          data-testid={`edit-user-${u.email}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteUser(u.id)}
                          disabled={u.id === currentUser?.id}
                          data-testid={`delete-user-${u.email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Create/Edit Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-lg bg-white text-gray-900 border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 font-bold">
              {selectedUser ? 'Edit User' : 'Create New User'}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedUser ? 'Update user account details' : 'Create a new judge or admin account'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={selectedUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                required
                placeholder="John Doe"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="user-name-input"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                required
                placeholder="judge@example.com"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="user-email-input"
              />
            </div>
            
            <div>
              <Label>{selectedUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                required={!selectedUser}
                placeholder={selectedUser ? '••••••••' : 'Enter password'}
                className="bg-white border-gray-300 text-gray-900"
                data-testid="user-password-input"
              />
            </div>
            
            <div>
              <Label>Role *</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({...userForm, role: value})}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="judge">Judge</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {currentUser?.role === 'super_admin' && (
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {userForm.role === 'judge' && (
              <div>
                <Label>Event Access</Label>
                <div className="space-y-2 mt-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {events.length === 0 ? (
                    <p className="text-gray-600 text-sm">No events available</p>
                  ) : (
                    events.map(event => (
                      <label key={event.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userForm.event_access.includes(event.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserForm({...userForm, event_access: [...userForm.event_access, event.id]});
                            } else {
                              setUserForm({...userForm, event_access: userForm.event_access.filter(id => id !== event.id)});
                            }
                          }}
                          className="rounded border-gray-300 bg-gray-100 text-pink-600 focus:ring-red-500"
                        />
                        <span className="text-gray-900 text-sm">{event.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-gray-600 text-xs mt-1">Select which events this judge can access</p>
              </div>
            )}

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="user-submit-btn">
                {submitting ? 'Saving...' : selectedUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
